import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, MedicalServices } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { updateChildDietary } from "apis";
import { ALLERGENS, IChildDietary, SEVERITIES } from "types";

interface Props {
  open: boolean;
  childId: string | number;
  initial?: IChildDietary | null;
  onClose: (saved?: IChildDietary) => void;
}

const ChildDietaryDialog: React.FC<Props> = ({
  open,
  childId,
  initial,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [allergies, setAllergies] = useState<string[]>(
    initial?.allergies ?? [],
  );
  const [allergyOther, setAllergyOther] = useState(initial?.allergyOther ?? "");
  const [severity, setSeverity] = useState(initial?.severity ?? "");
  const [preferences, setPreferences] = useState(initial?.preferences ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    setSubmitting(true);
    updateChildDietary(childId, {
      allergies,
      allergyOther: allergyOther.trim(),
      severity,
      preferences: preferences.trim(),
      notes: notes.trim(),
    })
      .then((saved) => {
        enqueueSnackbar(t("dietary.saved"), { variant: "success" });
        onClose(saved as IChildDietary);
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <MedicalServices />
          <Typography variant="h6" fontWeight={700}>
            {t("dietary.edit")}
          </Typography>
        </Box>
        <IconButton
          onClick={() => onClose()}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3 }}>
        <Stack spacing={2.5} mt={0.5}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("dietary.allergies")}</InputLabel>
            <Select
              multiple
              value={allergies}
              onChange={(e) =>
                setAllergies(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as string[]),
                )
              }
              input={<OutlinedInput label={t("dietary.allergies")} />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((v) => (
                    <Chip
                      key={v}
                      size="small"
                      label={t(`menu.allergen_list.${v}`)}
                    />
                  ))}
                </Box>
              )}
            >
              {ALLERGENS.map((a) => (
                <MenuItem key={a} value={a}>
                  {t(`menu.allergen_list.${a}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t("dietary.allergy_other")}
            value={allergyOther}
            onChange={(e) => setAllergyOther(e.target.value)}
            fullWidth
            size="small"
          />

          <FormControl fullWidth size="small">
            <InputLabel>{t("dietary.severity")}</InputLabel>
            <Select
              value={severity}
              label={t("dietary.severity")}
              onChange={(e) => setSeverity(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("notSpecified")}</em>
              </MenuItem>
              {SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`dietary.severity_levels.${s}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t("dietary.preferences")}
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            placeholder={t("dietary.preferences_hint")}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />

          <TextField
            label={t("dietary.notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>

      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={() => onClose()}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={submitting}
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("buttons.save")
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ChildDietaryDialog;
